import { useState } from "react";
import { useNavigate } from "react-router-dom";
import { RefreshCw } from "lucide-react";
import { Badge, Banner, Button, Spinner, Table, type Column } from "../../components";
import { useCreateBatchRun, usePracticeClients, useSyncPracticeClients } from "../../api/hooks";
import { formatDateTime, type PracticeClient } from "../../api/types";
import { ApiError } from "../../api/errors";
import { t } from "../../i18n/t";

/** P5-web §2d. `sync_practice_clients` currently answers `503 upstream_unavailable` in a real deployment — the
 * reader is not wired (P5-backend §"Practice operations") — and this tab renders that as an informational
 * banner, not an error toast, because it is a known and temporary gap rather than a failure of this request. */
export function ClientsTab({ practiceId }: { practiceId: string }): JSX.Element {
  const [cursors, setCursors] = useState<(string | null)[]>([null]);
  const cursor = cursors[cursors.length - 1] ?? null;
  const clients = usePracticeClients(practiceId, cursor);
  const sync = useSyncPracticeClients(practiceId);
  const createBatchRun = useCreateBatchRun(practiceId);
  const navigate = useNavigate();

  const [selected, setSelected] = useState<Set<string>>(new Set());
  const [syncNotice, setSyncNotice] = useState<string | null>(null);
  const [selectError, setSelectError] = useState<string | null>(null);

  async function onSync(): Promise<void> {
    setSyncNotice(null);
    try {
      await sync.mutateAsync();
      setSyncNotice(t("practice.clients.sync_started"));
    } catch (caught) {
      if (caught instanceof ApiError && caught.is("upstream_unavailable")) {
        // The standing note beside the button already says exactly this — no second banner.
        setSyncNotice(null);
      } else {
        setSyncNotice(t("error.unexpected"));
      }
    }
  }

  async function onCreateBatchRun(): Promise<void> {
    setSelectError(null);
    if (selected.size === 0) {
      setSelectError(t("practice.clients.select_at_least_one"));
      return;
    }
    const created = await createBatchRun.mutateAsync({
      client_ids: [...selected],
      cap: 25,
      kind: "MIGRATION",
    });
    navigate(`/practice/${practiceId}/batch-runs/${created.batch_run_id}`);
  }

  function toggle(clientId: string): void {
    setSelected((prev) => {
      const next = new Set(prev);
      if (next.has(clientId)) next.delete(clientId);
      else next.add(clientId);
      return next;
    });
  }

  const columns: readonly Column<PracticeClient>[] = [
    {
      key: "select",
      header: t("practice.clients.select_all"),
      hiddenHeader: true,
      render: (row) => (
        <input
          type="checkbox"
          aria-label={row.display_name}
          checked={selected.has(row.client_id)}
          onChange={() => toggle(row.client_id)}
        />
      ),
    },
    {
      key: "name",
      header: t("practice.clients.col_name"),
      render: (row) => <span className="cellvendor__name">{row.display_name}</span>,
    },
    {
      key: "status",
      header: t("practice.clients.col_status"),
      render: (row) => (
        <Badge tone={row.status === "ACTIVE" ? "pass" : row.status === "ARCHIVED" ? "neutral" : "run"} dot>
          {row.status}
        </Badge>
      ),
    },
    {
      key: "cdd",
      header: t("practice.clients.col_cdd"),
      render: (row) => <Badge tone={row.cdd_status === "VERIFIED" ? "pass" : "warn"}>{row.cdd_status}</Badge>,
    },
    {
      key: "synced",
      header: t("practice.clients.col_synced"),
      render: (row) => <span className="mono muted">{formatDateTime(row.synced_at)}</span>,
    },
  ];

  return (
    <section className="card card--flush" aria-label={t("practice.clients.caption")}>
      <div className="chiprow">
        <div className="row">
          <Button size="compact" onClick={() => void onSync()} disabled={sync.isPending}>
            <RefreshCw size={16} strokeWidth={2} aria-hidden="true" />
            {t("practice.clients.refresh")}
          </Button>
          {/* The reader is not wired (P5-backend §"Practice operations") — said inline, not as an error. */}
          <span className="muted">{t("practice.clients.sync_pending")}</span>
        </div>
        <div className="row">
          <span className="selectedcount">
            {t("practice.clients.selected_count", { count: selected.size })}
          </span>
          <Button
            size="compact"
            variant="primary"
            onClick={() => void onCreateBatchRun()}
            disabled={createBatchRun.isPending}
          >
            {t("practice.clients.create_batch_run")}
          </Button>
        </div>
      </div>
      {syncNotice === null ? null : (
        <div className="card__body">
          <Banner tone="info">{syncNotice}</Banner>
        </div>
      )}
      {selectError === null ? null : (
        <div className="card__body">
          <Banner tone="warn">{selectError}</Banner>
        </div>
      )}

      {clients.isPending ? (
        <Spinner />
      ) : (
        <Table
          bare
          caption={t("practice.clients.caption")}
          columns={columns}
          rows={clients.data?.items ?? []}
          rowKey={(row) => row.client_id}
          emptyMessage={t("practice.clients.empty")}
          rowClassName={(row) => (selected.has(row.client_id) ? "is-selected" : undefined)}
        />
      )}

      <div className="table-pager">
        <Button
          size="compact"
          disabled={cursors.length === 1}
          onClick={() => setCursors((prev) => prev.slice(0, -1))}
        >
          {t("practice.clients.prev_page")}
        </Button>
        <Button
          size="compact"
          disabled={(clients.data?.next_cursor ?? null) === null}
          onClick={() => setCursors((prev) => [...prev, clients.data?.next_cursor ?? null])}
        >
          {t("practice.clients.next_page")}
        </Button>
      </div>
    </section>
  );
}
